<template>
  <div>
    <Header />

    <!-- Page Header -->
    <div class="back-row">
      <button class="btn-back" @click="$router.back()">
        <font-awesome-icon icon="arrow-left" /> Back
      </button>
    </div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Create Payrun</h5>
        <p class="page-sub">
          <router-link to="/payroll" class="breadcrumb-link">Payroll</router-link>
          <span class="breadcrumb-sep">/</span>
          <span>Create Payrun</span>
        </p>
      </div>
    </div>

    <!-- Loading overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
    </div>

    <!-- STEPPER -->
    <div class="stepper-card">
      <div class="stepper">
        <template v-for="(step, i) in steps" :key="step.number">
          <div class="step">
            <div class="step-circle"
              :class="{
                'step-circle--done':    currentStep > step.number,
                'step-circle--active':  currentStep === step.number,
                'step-circle--pending': currentStep < step.number,
              }">
              <font-awesome-icon v-if="currentStep > step.number" icon="check" />
              <font-awesome-icon v-else :icon="step.icon" />
            </div>
            <span class="step-label"
              :class="currentStep >= step.number ? 'step-label--active' : 'step-label--pending'">
              {{ step.title }}
            </span>
          </div>
          <div v-if="i < steps.length - 1" class="step-line"
            :class="currentStep > step.number ? 'step-line--done' : 'step-line--pending'" />
        </template>
      </div>
    </div>


    <!-- FORM CARD -->
    <div class="form-card">

      <!-- ══ STEP 1: Period ══ -->
      <div v-if="currentStep === 1">
        <h3 class="step-title">Define Payroll Period</h3>
        <div class="fields-grid fields-grid--2">
          <div class="field-group">
            <label class="field-label">Start Date</label>
            <input type="date" class="field-input" v-model="period.startDate" :max="today" />
          </div>
          <div class="field-group">
            <label class="field-label">End Date</label>
            <input type="date" class="field-input" v-model="period.endDate" :max="currentMonthEnd" />
          </div>
          <div class="field-group">
            <label class="field-label">Pay Frequency</label>
            <select class="field-input field-select" v-model="period.frequency">
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
            </select>
          </div>
          <div class="field-group">
            <label class="field-label">Payroll Month</label>
            <select class="field-input field-select" v-model="period.payroll_month">
              <option value="" disabled selected>-- select --</option>
              <option value="January">January</option>
              <option value="February">February</option>
              <option value="March">March</option>
              <option value="April">April</option>
              <option value="May">May</option>
              <option value="June">June</option>
              <option value="July">July</option>
              <option value="August">August</option>
              <option value="September">September</option>
              <option value="October">October</option>
              <option value="November">November</option>
              <option value="December">December</option>
            </select>
          </div>

          <div class="field-group ">
            <label class="field-label">Scottish Payroll <span class="opt">(UK only)</span></label>
            <label class="scotland-toggle">
              <input type="checkbox" class="row-check" v-model="period.isScotland" />
              <span>Apply Scottish Rate of Income Tax (SRIT)</span>
            </label>
          </div>
          <div class="field-group">
            <label class="field-label">Expected Pay Date</label>
            <input type="date" class="field-input" v-model="period.payment_date"/>
          </div>
          
          <div class="field-group field-group--full">
            <label class="field-label">Notes <span class="opt">(optional)</span></label>
            <textarea class="field-input" rows="3"
              placeholder="e.g., June 2026 monthly payroll"
              v-model="period.notes"></textarea>
          </div>
        </div>
      </div>


      <!-- ══ STEP 2: Employees ══ -->
      <div v-if="currentStep === 2">
        <div class="step-hd">
          <h3 class="step-title">Select Employees</h3>
          <span class="step-count">{{ selectedEmployees.length }} of {{ allEmployees.length }} selected</span>
        </div>

        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th class="col-check">
                  <input type="checkbox" class="row-check"
                    :checked="selectedEmployees.length === allEmployees.length && allEmployees.length > 0"
                    @change="toggleAllEmployees" />
                </th>
                <th>Employee</th>
                <th>Department</th>
                <th>Pay Grade</th>
                <th>Base Salary</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="emp in allEmployees" :key="emp.id"
                class="row-clickable"
                :class="{ 'row--selected': isEmployeeSelected(emp.id) }"
                @click="toggleEmployee(emp)">
                <td class="col-check" @click.stop>
                  <input type="checkbox" class="row-check"
                    :checked="isEmployeeSelected(emp.id)"
                    @change="toggleEmployee(emp)" />
                </td>
                <td>
                  <div class="emp-cell">
                    <div class="emp-avatar">{{ getInitials(emp.firstName, emp.lastName) }}</div>
                    <span class="emp-name">{{ emp.firstName }} {{ emp.lastName }}</span>
                  </div>
                </td>
                <td class="text-muted">{{ emp.department }}</td>
                <td class="text-muted">{{ emp.paygrade }}</td>
                <td class="text-muted">{{ formatCurrency(emp.baseSalary) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>


      <!-- ══ STEP 3: Adjustments ══ -->
      <div v-if="currentStep === 3">
        <div class="step-hd">
          <div>
            <h3 class="step-title">Earnings &amp; Deductions</h3>
            <p class="step-sub">Optional — click Next to skip.</p>
          </div>
          <button class="btn-primary-action" @click="addAdjustment">
            <font-awesome-icon icon="plus" class="me-1" /> Add Adjustment
          </button>
        </div>

        <div v-if="adjustments.length === 0" class="empty-state">
          <font-awesome-icon icon="sliders-h" class="empty-icon" />
          <p>No adjustments added.</p>
          <p class="empty-hint">Click "Add Adjustment" to add earnings or deductions, or "Next" to skip.</p>
        </div>

        <div v-else class="adj-list">
          <div v-for="adj in adjustments" :key="adj.id" class="adj-card">
            <div class="adj-fields">
              <!-- Type -->
              <div class="field-group">
                <label class="field-label-sm">Type</label>
                <select class="field-input field-select" v-model="adj.type">
                  <option value="earning">Earning</option>
                  <option value="deduction">Deduction</option>
                </select>
              </div>

              <!-- Calculation type -->
              <div class="field-group">
                <label class="field-label-sm">Calculation</label>
                <select class="field-input field-select" v-model="adj.calculationType">
                  <option value="fixed">Fixed ({{ currencySymbol }})</option>
                  <option value="percentage">Percentage (%)</option>
                </select>
              </div>

              <!-- Amount -->
              <div class="field-group">
                <label class="field-label-sm">
                  {{ adj.calculationType === 'percentage' ? 'Rate (%)' : `Amount (${currencySymbol})` }}
                </label>
                <div class="adj-amount-wrap">
                  <span class="adj-amount-prefix">
                    {{ adj.calculationType === 'percentage' ? '%' : currencySymbol }}
                  </span>
                  <input type="number" class="field-input adj-amount-input"
                    v-model="adj.amount" min="0"
                    :max="adj.calculationType === 'percentage' ? 100 : undefined"
                    :step="adj.calculationType === 'percentage' ? 0.01 : 1"
                    placeholder="0" />
                </div>
              </div>

              <!-- Title -->
              <div class="field-group adj-title-field">
                <label class="field-label-sm">Title</label>
                <input type="text" class="field-input" v-model="adj.title"
                  placeholder="e.g. Bonus, Loan deduction" />
              </div>

              <!-- Taxable checkbox + Remove -->
              <div class="adj-meta">
                <label class="taxable-label"
                  :class="{ 'taxable-label--disabled': adj.type === 'deduction' }"
                  :title="adj.type === 'deduction' ? 'Deductions are not taxable' : ''">
                  <input type="checkbox"
                    class="row-check"
                    v-model="adj.is_taxable"
                    :disabled="adj.type === 'deduction'" />
                  <span>Taxable</span>
                </label>
                <button class="btn-remove" @click="removeAdjustment(adj.id)" title="Remove">
                  <font-awesome-icon icon="trash" />
                </button>
              </div>
            </div>

            <!-- Apply To -->
            <div class="apply-to-section">
              <label class="field-label-sm">Apply To</label>
              <div class="apply-to-box">
                <div class="radio-row">
                  <label class="radio-label">
                    <input type="radio" :name="'applyTo-' + adj.id"
                      :checked="adj.applyTo === 'all'"
                      @change="updateAdj(adj.id, { applyTo: 'all', employeeIds: [] })" />
                    All Employees
                  </label>
                  <label class="radio-label">
                    <input type="radio" :name="'applyTo-' + adj.id"
                      :checked="adj.applyTo === 'selected'"
                      @change="updateAdj(adj.id, { applyTo: 'selected', employeeIds: [] })" />
                    Selected Employees
                  </label>
                </div>

                <div v-if="adj.applyTo === 'selected'" class="emp-picker">
                  <label class="emp-picker-all">
                    <input type="checkbox"
                      :checked="adj.employeeIds.length === selectedEmployees.length && selectedEmployees.length > 0"
                      @change="(e) => updateAdj(adj.id, { employeeIds: e.target.checked ? selectedEmployees.map(e => e.id) : [] })" />
                    <span class="emp-picker-all-label">Select All</span>
                    <span class="emp-picker-count">{{ adj.employeeIds.length }} / {{ selectedEmployees.length }}</span>
                  </label>
                  <div class="emp-picker-list">
                    <label v-for="emp in selectedEmployees" :key="emp.id" class="emp-picker-row">
                      <input type="checkbox"
                        :checked="adj.employeeIds.includes(emp.id)"
                        @change="(e) => toggleAdjEmployee(adj.id, emp.id, e.target.checked)" />
                      <div class="emp-avatar emp-avatar--sm">{{ getInitials(emp.firstName, emp.lastName) }}</div>
                      <div>
                        <p class="emp-name">{{ emp.firstName }} {{ emp.lastName }}</p>
                        <p class="emp-dept">{{ emp.department }}</p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      <!-- ══ STEP 4: Statutory Deductions + Confirm ══ -->
      <div v-if="currentStep === 4">
        <div class="mb-5">
          <h3 class="step-title">Statutory Deductions</h3>
          <p class="step-sub">Select which statutory deductions apply. Rates are editable.</p>
        </div>

        <div v-if="statutoryDeductions.length === 0" class="empty-state empty-state--sm">
          No statutory deductions configured. Contact your administrator.
        </div>

        <template v-else>
          <!-- Employee deductions -->
          <div v-if="employeeStatutories.length > 0" class="statutory-section">
            <p class="statutory-section-title">Employee Deductions</p>
            <div class="statutory-list">
              <div v-for="ded in employeeStatutories" :key="ded.id"
                class="statutory-row"
                :class="{ 'statutory-row--active': ded.enabled }">
                <input type="checkbox" class="row-check"
                  :id="'stat-' + ded.id"
                  v-model="ded.enabled" />
                <label :for="'stat-' + ded.id" class="statutory-label">
                  <span class="statutory-name">{{ ded.name }}</span>
                  <span class="statutory-desc">{{ ded.description }}</span>
                </label>
                <div class="statutory-rate">
                  <span v-if="ded.percent <= 0" class="rate-calculated">Calculated</span>
                  <template v-else>
                    <input type="number" class="field-input rate-input"
                      v-model.number="ded.rate"
                      :disabled="!ded.enabled"
                      min="0" max="100" step="0.01" />
                    <span class="rate-pct">%</span>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Employer contributions -->
          <div v-if="employerStatutories.length > 0" class="statutory-section">
            <p class="statutory-section-title statutory-section-title--employer">Employer Contributions</p>
            <div class="statutory-list">
              <div v-for="ded in employerStatutories" :key="ded.id"
                class="statutory-row statutory-row--employer"
                :class="{ 'statutory-row--employer-active': ded.enabled }">
                <input type="checkbox" class="row-check"
                  :id="'stat-' + ded.id"
                  v-model="ded.enabled" />
                <label :for="'stat-' + ded.id" class="statutory-label">
                  <span class="statutory-name">{{ ded.name }}</span>
                  <span class="statutory-desc">{{ ded.description }}</span>
                </label>
                <div class="statutory-rate">
                  <span v-if="ded.percent <= 0" class="rate-calculated">Calculated</span>
                  <template v-else>
                    <input type="number" class="field-input rate-input"
                      v-model.number="ded.rate"
                      :disabled="!ded.enabled"
                      min="0" max="100" step="0.01" />
                    <span class="rate-pct">%</span>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Summary card -->
        <div class="summary-card">
          <p class="summary-title">Payrun Summary</p>
          <div class="summary-rows">
            <div class="summary-row">
              <span>Period</span>
              <span class="summary-val">{{ period.startDate }} → {{ period.endDate }}</span>
            </div>
            <div class="summary-row">
              <span>Pay Frequency</span>
              <span class="summary-val" style="text-transform:capitalize">{{ period.frequency }}</span>
            </div>
            <div class="summary-row" v-if="period.isScotland">
              <span>Tax Region</span>
              <span class="summary-val">Scotland (SRIT)</span>
            </div>
            <div class="summary-row">
              <span>Employees</span>
              <span class="summary-val">{{ selectedEmployees.length }}</span>
            </div>
            <div class="summary-row">
              <span>Additional Earning & Deductions</span>
              <span class="summary-val">{{ adjustments.length }}</span>
            </div>
            <div class="summary-row">
              <span>Statutory Deductions</span>
              <span class="summary-val">{{ enabledStatutoryCount }} active</span>
            </div>
          </div>
          <div v-if="enabledStatutoryCount > 0" class="summary-tags">
            <span v-for="d in activeStatutory" :key="d.id" class="stat-tag">
              {{ d.name }} — {{ d.percent == 0 ? 'Calculated' : d.rate + '%' }}
            </span>
          </div>
        </div>
      </div>


      <!-- ══ STEP 5: Review ══ -->
      <div v-if="currentStep === 5 && summary">
        <h3 class="step-title mb-5">Payroll Review</h3>
        <div class="review-cards">
          <div class="review-card">
            <p class="review-card-label">Total Employees</p>
            <p class="review-card-val">{{ summary.employeeCount }}</p>
          </div>
          <div class="review-card">
            <p class="review-card-label">Total Gross Pay</p>
            <p class="review-card-val">{{ formatCurrency(summary.totalIncome) }}</p>
          </div>
          <div class="review-card review-card--red">
            <p class="review-card-label">Total Deductions</p>
            <p class="review-card-val review-card-val--red">{{ formatCurrency(summary.totalDeductions) }}</p>
          </div>
          <div class="review-card review-card--green">
            <p class="review-card-label">Total Net Pay</p>
            <p class="review-card-val review-card-val--green">{{ formatCurrency(summary.totalPayable) }}</p>
          </div>
        </div>
        <div class="review-action">
          <button class="btn-view-report" @click="$router.push('/payroll/' + payrunId)">
            View Detailed Report <font-awesome-icon icon="arrow-right" class="ms-1" />
          </button>
        </div>
      </div>

    </div><!-- /form-card -->


    <!-- NAVIGATION -->
    <div class="form-nav">
      <button class="btn-outline"
        :disabled="currentStep === 1 || currentStep === 5"
        @click="handleBack">
        <font-awesome-icon icon="arrow-left" class="me-1" /> Back
      </button>
      <button v-if="currentStep < 5"
        class="btn-primary-action"
        :disabled="isLoading"
        @click="handleNext">
        <span v-if="isLoading && currentStep === 4"
          class="spinner-border spinner-border-sm me-1" role="status"></span>
        {{ currentStep === 4 ? 'Calculate Now' : 'Next' }}
        <font-awesome-icon icon="arrow-right" class="ms-1" />
      </button>
    </div>

  </div>
</template>


<script>
import Header from './component/header.vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  name: 'CreatePayrunPage',

  components: { Header },

  data() {
    const now        = new Date();
    const firstDay   = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay    = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const fmt        = (d) => d.toISOString().split('T')[0];
    const monthNames = ['January','February','March','April','May','June',
                        'July','August','September','October','November','December'];
    return {
      currentStep: 1,
      isLoading:   false,

      steps: [
        { number: 1, title: 'Period',      icon: 'calendar' },
        { number: 2, title: 'Employees',   icon: 'users' },
        { number: 3, title: 'Adjustments', icon: 'money-bill-wave' },
        { number: 4, title: 'Calculate',   icon: 'calculator' },
        { number: 5, title: 'Review',      icon: 'eye' },
      ],

      // Step 1
      period: {
        startDate: fmt(firstDay),
        endDate:   fmt(lastDay),
        notes:     '',
        frequency:  'monthly',
        payroll_month: '',
        payment_date: '',
        isScotland: false,
        month:      monthNames[now.getMonth()],
        year:      String(now.getFullYear()),
      },

      // Step 2
      allEmployees:      [],
      selectedEmployees: [],

      // Step 3
      adjustments: [],

      // Step 4
      statutoryDeductions: [],

      // Step 5
      payrunId: null,
      summary:  null,

      businessCountryCode: '',

      toast: useToast(),
    };
  },

  computed: {
    today() {
      return new Date().toISOString().split('T')[0];
    },

    currentMonthEnd() {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    },

    employeeStatutories() {
      return this.statutoryDeductions.filter(d => d.type === 'employee');
    },

    employerStatutories() {
      return this.statutoryDeductions.filter(d => d.type === 'employer');
    },

    enabledStatutoryCount() {
      return this.statutoryDeductions.filter(d => d.enabled).length;
    },

    activeStatutory() {
      return this.statutoryDeductions.filter(d => d.enabled);
    },

    currencySymbol() {
      const map = { GB: '£', US: '$', EU: '€' };
      return map[this.businessCountryCode] || '₦';
    },

    currencyLocale() {
      const map = { GB: 'en-GB', US: 'en-US' };
      return map[this.businessCountryCode] || 'en-NG';
    },
  },

  created() {
    this.fetchEmployees();
    this.fetchStatutory();
    this.fetchBusinessCountry();
  },

  methods: {
    // -- Data ----------------------------------------------------------
    fetchBusinessCountry() {
      axios.get('/admin/business/user/active-business')
        .then(({ data }) => {
          const biz = data?.data || data || {};
          this.businessCountryCode = biz.country_code || '';
        })
        .catch(() => {});
    },

    fetchEmployees() {
      this.isLoading = true;
      axios.get('/payroll/employees/active')
        .then(({ data }) => {
          const rows = Array.isArray(data.data?.data) ? data.data.data
            : Array.isArray(data.data) ? data.data : [];
          this.allEmployees = rows.map(this.mapEmployee).filter(e => e.id);
        })
        .catch(() => { this.toast.error('Could not load employees.'); })
        .finally(() => { this.isLoading = false; });
    },

    fetchStatutory() {
      axios.get('/payroll/statutory/active')
        .then(({ data }) => {
          const rows = Array.isArray(data.data?.data) ? data.data.data
            : Array.isArray(data.data) ? data.data : [];
          this.statutoryDeductions = rows.map(item => ({
            id:          item?.id ?? '',
            name:        item?.name ?? '',
            description: item?.description ?? '',
            type:        item?.type ?? 'employee',
            percent:     item?.percent ?? 0,
            rate:        item?.percent ?? 0,
            enabled:     true,
          }));
        })
        .catch(() => {});
    },

    mapEmployee(emp) {
      const components = Array.isArray(emp?.paygrade?.components)
        ? emp.pay_grade.components : [];
        const baseSalary =
              Number(emp?.basic_salary) ||
              Number(emp?.paygrade?.basic_salary) ||
              0;
      const salaryComponentDeductions = components
        .filter(c => c.type === 'deduction')
        .reduce((sum, c) => {
          if (c.calculation_type === 'percentage') return sum + (baseSalary * Number(c.value || 0)) / 100;
          return sum + Number(c.value || 0);
        }, 0);
      return {
        id:                       emp?.id,
        user_id:                  emp?.user_id,
        firstName:                emp?.first_name  || '',
        lastName:                 emp?.last_name   || '',
        department:               emp?.department?.name || '--',
        paygrade:                 emp?.paygrade?.name  || '--',
        baseSalary,
        salaryComponentDeductions,
      };
    },

    // -- Employee selection --------------------------------------------
    isEmployeeSelected(id) {
      return this.selectedEmployees.some(e => e.id === id);
    },

    toggleEmployee(emp) {
      if (this.isEmployeeSelected(emp.id)) {
        this.selectedEmployees = this.selectedEmployees.filter(e => e.id !== emp.id);
      } else {
        this.selectedEmployees = [...this.selectedEmployees, emp];
      }
    },

    toggleAllEmployees(e) {
      this.selectedEmployees = e.target.checked ? [...this.allEmployees] : [];
    },

    // -- Adjustments ---------------------------------------------------
    addAdjustment() {
      this.adjustments.push({
        id:             Date.now(),
        type:           'earning',
        calculationType:'fixed',
        is_taxable:     false,
        applyTo:        'all',
        amount:         0,
        title:          '',
        employeeIds:    [],
      });
    },

    removeAdjustment(id) {
      this.adjustments = this.adjustments.filter(a => a.id !== id);
    },

    updateAdj(id, patch) {
      this.adjustments = this.adjustments.map(a => a.id === id ? { ...a, ...patch } : a);
    },

    toggleAdjEmployee(adjId, empId, checked) {
      const adj = this.adjustments.find(a => a.id === adjId);
      if (!adj) return;
      const ids = checked
        ? [...adj.employeeIds, empId]
        : adj.employeeIds.filter(id => id !== empId);
      this.updateAdj(adjId, { employeeIds: ids });
    },

    // -- Navigation ----------------------------------------------------
    handleBack() {
      if (this.currentStep > 1 && this.currentStep < 5) this.currentStep--;
    },

    handleNext() {
      if (this.currentStep === 1) {
        if (this.period.startDate > this.today) {
          this.toast.error('Start date cannot be in the future.');
          return;
        }
        if (this.period.endDate > this.currentMonthEnd) {
          this.toast.error('End date cannot cross into the next month.');
          return;
        }
        this.currentStep = 2;
        return;
      }

      if (this.currentStep === 2) {
        if (this.selectedEmployees.length === 0) {
          this.toast.error('Please select at least one employee.');
          return;
        }
        this.currentStep = 3;
        return;
      }

      if (this.currentStep === 3) {
        for (const adj of this.adjustments) {
          if (adj.applyTo === 'selected' && adj.employeeIds.length === 0) {
            this.toast.error(`Select at least one employee for adjustment "${adj.title || 'Untitled'}".`);
            return;
          }
        }
        this.currentStep = 4;
        return;
      }

      if (this.currentStep === 4) {
        this.submitPayrun();
      }
    },

    // -- Submit --------------------------------------------------------
    submitPayrun() {
      this.isLoading = true;

      const payload = {
        payroll_period: {
          start_date:       this.period.startDate,
          end_date:         this.period.endDate,
          name:             this.period.notes || `${this.period.month} ${this.period.year} payroll`,
          period_frequency: this.period.frequency,
          is_scotland:      this.period.isScotland,
          payroll_month:    this.period.payroll_month,
          payment_date: this.period.payment_date,
        },

        employees: this.selectedEmployees.map(e => ({
          user_id:          e.user_id || e.id,
          employee_id:      e.id,
          gross_pay:        Number(e.baseSalary),
          net_pay:          Number(e.baseSalary) - Number(e.salaryComponentDeductions),
          total_deductions: Number(e.salaryComponentDeductions),
        })),

        adjustments: this.adjustments.map(adj => ({
          title:            adj.title,
          type:             adj.type,
          calculation_type: adj.calculationType,
          is_taxable:       adj.is_taxable,
          amount:           Number(adj.amount),
          apply_to:         adj.applyTo,
          employee_ids:     adj.applyTo === 'all'
            ? this.selectedEmployees.map(e => e.id)
            : adj.employeeIds,
        })),

        statutory_deductions: this.activeStatutory.map(d => ({
          id:   d.id,
          name: d.name,
          rate: d.rate,
          type: 'percentage',
        })),

        payrun_ref: this.$route.query.payrunRef ?? null,
      };

      axios.post('/payroll/calculate', payload)
        .then(({ data }) => {
          const calc = data?.data || {};
          this.payrunId = calc?.reference ?? null;
          this.summary  = {
            totalIncome:      Number(calc?.total_gross_pay   ?? 0),
            totalDeductions:  Number(calc?.total_deductions  ?? 0),
            totalPayable:     Number(calc?.total_net_pay     ?? 0),
            employeeCount:    Number(calc?.total_employees   ?? this.selectedEmployees.length),
          };
          this.currentStep = 5;
          this.toast.success('Payroll calculated successfully.');
        })
        .catch(err => {
          const msg = err.response?.data?.message ?? 'Failed to calculate payroll.';
          this.toast.error(msg);
        })
        .finally(() => { this.isLoading = false; });
    },

    // -- Helpers -------------------------------------------------------
    getInitials(first, last) {
      return ((first?.charAt(0) || '') + (last?.charAt(0) || '')).toUpperCase() || '?';
    },

    formatCurrency(val) {
      return this.currencySymbol + Number(val || 0).toLocaleString(this.currencyLocale, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      });
    },
  },
};
</script>


<style scoped>
/* -- Page ---------------------------------------------------------------- */
.back-row    { margin-bottom:12px; }
.btn-back    { display:inline-flex; align-items:center; gap:7px; background:transparent; border:none; font-size:13px; font-weight:600; color:#7a90b0; cursor:pointer; padding:0; transition:color .15s; }
.btn-back:hover { color:#004AAD; }
.page-header { margin-bottom:24px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; display:flex; align-items:center; gap:6px; }
.breadcrumb-link { color:#7a90b0; text-decoration:none; }
.breadcrumb-link:hover { color:#004AAD; }
.breadcrumb-sep { color:#c5d0e0; }
.mb-5 { margin-bottom:20px; }
.me-1 { margin-right:4px; }
.ms-1 { margin-left:4px; }
.opt  { font-size:11px; color:#94a3b8; font-weight:400; }

/* -- Loading overlay ----------------------------------------------------- */
.loading-overlay { position:fixed; inset:0; z-index:2000; background:rgba(255,255,255,.65); display:flex; align-items:center; justify-content:center; }

/* -- Stepper ------------------------------------------------------------- */
.stepper-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:28px 32px; margin-bottom:20px; }
.stepper      { display:flex; align-items:center; justify-content:center; }
.step         { display:flex; flex-direction:column; align-items:center; gap:8px; }
.step-circle  { width:44px; height:44px; border-radius:50%; border:2px solid; display:flex; align-items:center; justify-content:center; font-size:15px; font-weight:700; transition:all .2s; }
.step-circle--done    { background:#004AAD; border-color:#004AAD; color:#fff; }
.step-circle--active  { background:#fff; border-color:#004AAD; color:#004AAD; }
.step-circle--pending { background:#fff; border-color:#dde5f0; color:#94a3b8; }
.step-label           { font-size:12.5px; font-weight:600; white-space:nowrap; }
.step-label--active   { color:#1a2b45; }
.step-label--pending  { color:#94a3b8; }
.step-line            { width:60px; height:2px; margin:0 10px; margin-bottom:22px; flex-shrink:0; transition:background .2s; }
.step-line--done      { background:#004AAD; }
.step-line--pending   { background:#e4eaf2; }

/* -- Form card ----------------------------------------------------------- */
.form-card   { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:36px 40px; margin-bottom:20px; min-height:400px; }
.step-title  { font-size:20px; font-weight:800; color:#1a2b45; margin:0 0 20px; }
.step-sub    { font-size:13px; color:#7a90b0; margin:0 0 20px; }
.step-hd     { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.step-hd .step-title { margin:0; }
.step-count  { font-size:13px; color:#7a90b0; align-self:center; }

/* -- Fields grid --------------------------------------------------------- */
.fields-grid     { display:grid; gap:18px; }
.fields-grid--2  { grid-template-columns:1fr 1fr; }
.field-group     { display:flex; flex-direction:column; gap:5px; }
.field-group--full { grid-column:1/-1; }
.field-label     { font-size:13px; font-weight:600; color:#1a2b45; }
.field-label-sm  { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
.field-input     { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:9px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; }
.field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.field-input:disabled { background:#f4f7fb; color:#94a3b8; cursor:not-allowed; }
.field-select    { appearance:auto; cursor:pointer; }
textarea.field-input { resize:vertical; }

/* -- Table --------------------------------------------------------------- */
.table-wrap  { overflow-x:auto; border:1px solid #e4eaf2; border-radius:10px; overflow:hidden; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody tr.row--selected { background:#edf4ff; }
.data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
.col-check  { width:40px; }
.row-clickable { cursor:pointer; }
.row-check  { width:16px; height:16px; cursor:pointer; accent-color:#004AAD; }

/* -- Employee cell ------------------------------------------------------- */
.emp-cell   { display:flex; align-items:center; gap:10px; }
.emp-avatar { width:34px; height:34px; border-radius:50%; background:#004AAD; color:#fff; font-size:12px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.emp-avatar--sm { width:28px; height:28px; font-size:10px; }
.emp-name   { font-size:13px; font-weight:600; color:#1a2b45; margin:0; }
.emp-dept   { font-size:11px; color:#7a90b0; margin:0; }
.text-muted { color:#4a5e78; font-size:13px; }

/* -- Adjustments --------------------------------------------------------- */
.adj-list    { display:flex; flex-direction:column; gap:14px; }
.adj-card    { border:1px solid #e4eaf2; border-radius:10px; padding:20px; }
.adj-fields      { display:grid; grid-template-columns:120px 130px 130px 1fr auto; gap:12px; align-items:flex-end; margin-bottom:16px; }
.adj-title-field { min-width:0; }
.adj-remove      { display:flex; align-items:flex-end; }
.btn-remove      { width:34px; height:34px; border-radius:7px; border:1px solid #fecaca; background:#fff0f0; color:#e03535; display:flex; align-items:center; justify-content:center; cursor:pointer; font-size:13px; transition:all .15s; }
.btn-remove:hover { background:#e03535; color:#fff; }

/* Amount with prefix */
.adj-amount-wrap   { position:relative; display:flex; align-items:center; }
.adj-amount-prefix { position:absolute; left:10px; font-size:13px; color:#7a90b0; font-weight:600; pointer-events:none; z-index:1; }
.adj-amount-input  { padding-left:26px !important; }

/* Taxable + remove column */
.adj-meta          { display:flex; flex-direction:column; align-items:center; gap:6px; justify-content:flex-end; padding-bottom:2px; }
.taxable-label     { display:flex; align-items:center; gap:5px; font-size:12px; font-weight:600; color:#1a2b45; cursor:pointer; white-space:nowrap; }
.taxable-label input { accent-color:#004AAD; }
.taxable-label--disabled { opacity:.4; cursor:not-allowed; }
.scotland-toggle { display:inline-flex; align-items:center; gap:8px; font-size:13px; font-weight:500; color:#1a2b45; cursor:pointer; margin-top:4px; }
.scotland-toggle input { accent-color:#004AAD; width:16px; height:16px; cursor:pointer; }

/* Apply to */
.apply-to-section { margin-top:4px; }
.apply-to-box { background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:14px; }
.radio-row   { display:flex; gap:24px; }
.radio-label { display:flex; align-items:center; gap:7px; font-size:13px; font-weight:500; color:#1a2b45; cursor:pointer; }
.radio-label input { accent-color:#004AAD; }

/* Employee picker */
.emp-picker  { margin-top:12px; }
.emp-picker-all { display:flex; align-items:center; gap:10px; background:#f0f4f8; border-radius:7px; padding:8px 12px; cursor:pointer; margin-bottom:6px; }
.emp-picker-all input { accent-color:#004AAD; }
.emp-picker-all-label { font-size:13px; font-weight:600; color:#1a2b45; flex:1; }
.emp-picker-count { font-size:11.5px; color:#7a90b0; }
.emp-picker-list { max-height:200px; overflow-y:auto; display:flex; flex-direction:column; gap:4px; }
.emp-picker-row { display:flex; align-items:center; gap:10px; border:1px solid #f0f4f8; border-radius:7px; padding:8px 12px; cursor:pointer; transition:background .12s; }
.emp-picker-row:hover { background:#f4f7fb; }
.emp-picker-row input { accent-color:#004AAD; }

/* -- Statutory deductions ------------------------------------------------ */
.statutory-section       { margin-bottom:24px; }
.statutory-section-title { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; margin:0 0 10px; }
.statutory-section-title--employer { color:#9a6700; }
.statutory-list { display:flex; flex-direction:column; gap:8px; }
.statutory-row  { display:flex; align-items:center; gap:14px; border:1px solid #e4eaf2; border-radius:10px; padding:14px 16px; transition:all .15s; background:#fff; }
.statutory-row--active         { border-color:#004AAD; background:rgba(0,74,173,.04); }
.statutory-row--employer       { border-color:#fde68a; }
.statutory-row--employer-active { border-color:#f59e0b; background:rgba(245,158,11,.04); }
.statutory-label { flex:1; cursor:pointer; display:flex; flex-direction:column; gap:2px; }
.statutory-name  { font-size:13.5px; font-weight:600; color:#1a2b45; }
.statutory-desc  { font-size:12px; color:#7a90b0; }
.statutory-rate  { display:flex; align-items:center; gap:6px; flex-shrink:0; }
.rate-input      { width:80px; text-align:right; padding:7px 10px; font-size:13px; }
.rate-pct        { font-size:13px; color:#7a90b0; }
.rate-calculated { font-size:11.5px; font-weight:600; color:#004AAD; background:rgba(0,74,173,.08); border:1px solid rgba(0,74,173,.2); border-radius:20px; padding:3px 10px; white-space:nowrap; }

/* -- Summary card -------------------------------------------------------- */
.summary-card    { background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:20px 24px; }
.summary-title   { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; margin:0 0 14px; }
.summary-rows    { display:flex; flex-direction:column; gap:10px; }
.summary-row     { display:flex; align-items:center; justify-content:space-between; font-size:13.5px; color:#7a90b0; }
.summary-val     { font-weight:600; color:#1a2b45; }
.summary-tags    { display:flex; flex-wrap:wrap; gap:6px; margin-top:14px; padding-top:14px; border-top:1px solid #e4eaf2; }
.stat-tag        { display:inline-flex; align-items:center; background:rgba(0,74,173,.08); color:#004AAD; border:1px solid rgba(0,74,173,.2); font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; }

/* -- Review -------------------------------------------------------------- */
.review-cards    { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; margin-bottom:32px; }
.review-card     { background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:20px; }
.review-card--red   { background:#fff5f5; border-color:#fecaca; }
.review-card--green { background:#f0fdf4; border-color:#bbf7d0; }
.review-card-label  { font-size:13px; color:#7a90b0; margin:0 0 6px; }
.review-card-val    { font-size:24px; font-weight:800; color:#1a2b45; margin:0; }
.review-card-val--red   { color:#e03535; }
.review-card-val--green { color:#16a34a; }
.review-action   { display:flex; justify-content:center; }
.btn-view-report { display:inline-flex; align-items:center; gap:8px; background:#004AAD; color:#fff; font-size:14px; font-weight:700; padding:12px 32px; border-radius:10px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
.btn-view-report:hover { background:#003b8e; }

/* -- Empty state --------------------------------------------------------- */
.empty-state    { display:flex; flex-direction:column; align-items:center; padding:48px 24px; text-align:center; color:#7a90b0; }
.empty-state--sm { padding:24px; font-size:13px; border:1px dashed #dde5f0; border-radius:10px; margin-bottom:24px; }
.empty-icon     { font-size:36px; color:#c0c9d8; margin-bottom:12px; }
.empty-hint     { font-size:12.5px; color:#94a3b8; margin:4px 0 0; }

/* -- Buttons ------------------------------------------------------------- */
.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13.5px; font-weight:600; padding:10px 24px; border-radius:9px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.5; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13.5px; font-weight:600; padding:10px 22px; border-radius:9px; cursor:pointer; transition:background .15s; font-family:inherit; }
.btn-outline:hover:not(:disabled) { background:#f4f7fb; }
.btn-outline:disabled { opacity:.45; cursor:not-allowed; }
.form-nav { display:flex; justify-content:space-between; align-items:center; padding-top:4px; }

/* -- Responsive ---------------------------------------------------------- */
@media (max-width:900px)  { .review-cards { grid-template-columns:1fr 1fr; } }
@media (max-width:700px)  {
  .form-card   { padding:24px 20px; }
  .fields-grid--2 { grid-template-columns:1fr; }
  .field-group--full { grid-column:1; }
  .stepper-card { padding:20px 14px; }
  .step-line    { width:30px; }
  .adj-fields   { grid-template-columns:1fr 1fr; }
  .adj-title-field { grid-column:1/-1; }
  .adj-meta     { flex-direction:row; justify-content:space-between; width:100%; grid-column:1/-1; }
  .review-cards { grid-template-columns:1fr; }
}
</style>